import { useState } from 'react';
import { rupiah } from '@lib/currency';
import QRCode from 'react-qr-code';

import TimeCountdown from './TimeCountdown';
import AccordionItem from './Accordion';
import HeaderResult from '@components/Result/HeaderResult';
import { SettingsProps, DataDetailCheckout } from '@pages/types';
import { useGenerateNewSession } from '@pages/hooks/useGenerateNewSession';

interface QrisResult {
  data: DataDetailCheckout;
  settings: SettingsProps;
  merchantName?: string;
  onPaymentSelected?: () => void;
  onGoToOverview?: () => void;
  onRefreshData?: () => void;
}

const QrisResult: React.FC<QrisResult> = ({ data, onRefreshData }) => {
  const [accordionOpen, setAccordionOpen] = useState(true);
  const { mutate: generateNewSession, isPending: isGeneratingSession } =
    useGenerateNewSession(onRefreshData);

  const qris_info = [
    'Open your mobile banking app or e-wallet that supports QRIS.',
    'Select Scan QR / Pay with QRIS, then scan the QRIS code shown on the payment page.',
    `Confirm that the merchant name and amount are correct. Merchant: ${data?.merchant?.name ? data.merchant.name : '-'} , Total: ${rupiah(data.total) ? rupiah(data.total) : '-'}.`,
    'Confirm and authorize the payment within your app.',
    'You will receive a confirmation notification once the payment is successful.',
  ];

  return (
    <div className="flex flex-col gap-4">
      <HeaderResult
        title="QRIS"
        subtitle="Use the details below to complete your payment before the expiry time"
      />

      <TimeCountdown
        expirationTime={data?.payment?.expired_at}
        sessionLabel="Pay within 15 minutes"
        invoiceExpirationTime={data?.payment_link_expiration}
        onGenerateNewSession={() => generateNewSession()}
        isGeneratingSession={isGeneratingSession}
      />

      <div className="mx-auto my-6 h-auto w-[300px]">
        <QRCode
          size={100}
          style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
          value={
            data?.payment?.qris?.qr_content ? data.payment.qris.qr_content : '-'
          }
          viewBox={`0 0 100 100`}
        />
      </div>

      <div>
        <div className="flex flex-col gap-1">
          <AccordionItem
            title="QRIS Payment Instructions"
            content={
              <div className="flex flex-col gap-4">
                {qris_info.map((instruction, idx) => {
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg border border-gray-300 bg-white text-sm font-semibold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{instruction}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            }
            isOpen={accordionOpen}
            onToggle={() => setAccordionOpen(!accordionOpen)}
          />
        </div>
      </div>
    </div>
  );
};

export default QrisResult;
